import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Divider,
  Paper,
  Grid,
  LinearProgress,
  ImageList,
  ImageListItem,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { generateCollection, prepareLayersForGeneration, createDownloadableZip } from '../services/generatorService';
import { saveAs } from 'file-saver';
import { GeneratedImage, MetadataAttribute, Layer, LayerElement } from '../types';
import { PaymentStatus } from '../types';
import { getPricingTier, isPaymentRequired, isWalletWhitelisted, connectToMetaMask } from '../services/paymentService';
import PaymentModal from './PaymentModal';
import PricingInfo from './PricingInfo';

const GenerateCollection: React.FC = () => {
  const { 
    layers, 
    config, 
    generatedImages, 
    setGeneratedImages, 
    setCurrentStep, 
    isGenerating, 
    setIsGenerating,
    uniqueNFTs 
  } = useAppContext();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState<GeneratedImage | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(PaymentStatus.PENDING);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [modalKey, setModalKey] = useState(Date.now());
  const theme = useTheme();

  // Calculate the total number of NFTs
  const nftCount = config.layerConfigurations[0].growEditionSizeTo;
  
  // Get the pricing tier based on the number of NFTs
  const currentPricingTier = getPricingTier(nftCount);
  
  // Check if payment is required
  const needsPayment = isPaymentRequired(nftCount, walletAddress);
  console.log("Calcul de needsPayment:", { nftCount, walletAddress, needsPayment });

  // Update payment status when the component mounts or when the NFT count or wallet address changes
  useEffect(() => {
    console.log("useEffect déclenché - needsPayment:", needsPayment, "nftCount:", nftCount, "walletAddress:", walletAddress);
    setPaymentStatus(needsPayment ? PaymentStatus.PENDING : PaymentStatus.NOT_REQUIRED);
    setIsWhitelisted(isWalletWhitelisted(walletAddress));
  }, [needsPayment, nftCount, walletAddress]);

  // Fonction pour connecter le portefeuille et vérifier s'il est whitelisté
  const connectWallet = async () => {
    try {
      const address = await connectToMetaMask();
      console.log("Adresse connectée:", address);
      setWalletAddress(address);
      
      if (address) {
        const whitelisted = isWalletWhitelisted(address);
        console.log("Statut whitelist:", whitelisted);
        setIsWhitelisted(whitelisted);
        
        // Si le portefeuille est whitelisté, mettre à jour le statut de paiement
        if (whitelisted) {
          console.log("Portefeuille whitelisté, paiement non requis");
          setPaymentStatus(PaymentStatus.NOT_REQUIRED);
        } else {
          console.log("Portefeuille non whitelisté, vérification du paiement requis");
          const needsPayment = isPaymentRequired(nftCount, address);
          setPaymentStatus(needsPayment ? PaymentStatus.PENDING : PaymentStatus.NOT_REQUIRED);
        }
      }
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  };

  const handleGenerate = async () => {
    try {
      // Nettoyer le localStorage pour réinitialiser l'état du modal de paiement
      localStorage.removeItem('paymentModalStep');
      localStorage.removeItem('paymentStatus');
      localStorage.removeItem('transactionHash');
      
      setIsGenerating(true);
      setProgress(0);
      setError(null);
      
      // Check that all layers have at least one element
      const invalidLayers = layers.filter(layer => layer.elements.length === 0);
      if (invalidLayers.length > 0) {
        throw new Error(`The following layers have no elements: ${invalidLayers.map(l => l.name).join(', ')}`);
      }
      
      // Check that there are valid layers left after preparation if we need to generate procedural NFTs
      if (layers.filter(layer => layer.elements.length > 0).length === 0 && 
          (config.layerConfigurations[0].growEditionSizeTo - uniqueNFTs.length) > 0) {
        throw new Error('No valid layers with elements found. Please add elements to at least one layer or add more unique 1/1 NFTs to complete your collection.');
      }
      
      // Prepare layers for generation
      const preparedLayers = layers.map(layer => ({
        ...layer,
        elements: layer.elements.filter(el => el.image !== null)
      }));
      
      // Check if there are valid layers left after preparation if we need to generate procedural NFTs
      if (preparedLayers.filter(layer => layer.elements.length > 0).length === 0 && 
          (config.layerConfigurations[0].growEditionSizeTo - uniqueNFTs.length) > 0) {
        throw new Error('No valid layers with elements found. Please add elements to at least one layer or add more unique 1/1 NFTs to complete your collection.');
      }
      
      // Générer la collection en incluant les visuels uniques
      const results = await generateCollection(
        preparedLayers,
        config,
        uniqueNFTs,
        (progress) => setProgress(progress)
      );
      
      setGeneratedImages(results);
      
      // Reset payment status when a new collection is generated
      setPaymentStatus(PaymentStatus.PENDING);
      
      // Générer une nouvelle clé pour forcer la réinitialisation du modal
      setModalKey(Date.now());
    } catch (err) {
      console.error('Error generating collection:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while generating');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (forceDownload: boolean = false) => {
    if (generatedImages.length === 0) return;
    
    // Si forceDownload est true, on ignore les vérifications de paiement
    if (!forceDownload) {
      // Si l'utilisateur n'est pas connecté et que le paiement est requis, essayer de connecter le portefeuille d'abord
      if (!walletAddress && needsPayment) {
        try {
          await connectWallet();
          // Après la connexion, vérifier à nouveau si le paiement est requis
          if (isPaymentRequired(nftCount, walletAddress)) {
            setPaymentModalOpen(true);
            return;
          }
        } catch (error) {
          console.error("Error connecting wallet:", error);
          setError("Please connect your wallet to continue.");
          return;
        }
      }
      // Check if payment is required and not completed
      else if (needsPayment && paymentStatus !== PaymentStatus.COMPLETED) {
        setPaymentModalOpen(true);
        return;
      }
    }
    
    try {
      setIsDownloading(true);
      
      // Créer un ZIP avec les images et les métadonnées
      const zipBlob = await createDownloadableZip(generatedImages);
      
      // Télécharger le ZIP
      saveAs(zipBlob, `${config.namePrefix.replace(/\s+/g, '_')}_collection.zip`);
    } catch (err) {
      console.error('Error downloading collection:', err);
      setError('An error occurred while downloading');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(3);
  };

  const handleCloseError = () => {
    setError(null);
  };

  const handleOpenNFTDetails = (nft: GeneratedImage) => {
    setSelectedNFT(nft);
    setDialogOpen(true);
  };

  const handleCloseNFTDetails = () => {
    setDialogOpen(false);
    setSelectedNFT(null);
  };

  const handlePaymentComplete = () => {
    setPaymentStatus(PaymentStatus.COMPLETED);
    // Déclencher automatiquement le téléchargement après un paiement réussi
    handleDownload(true); // Forcer le téléchargement
  };

  // Calculate rarity for each attribute
  const calculateAttributeRarity = (attribute: MetadataAttribute): number => {
    if (!attribute || !generatedImages.length) return 0;
    
    // Count how many NFTs have this exact attribute
    const count = generatedImages.filter(img => 
      img.metadata.attributes.some(attr => 
        attr.trait_type === attribute.trait_type && attr.value === attribute.value
      )
    ).length;
    
    // Calculate percentage
    return (count / generatedImages.length) * 100;
  };

  // Get rarity color based on percentage
  const getRarityColor = (percentage: number): string => {
    if (percentage <= 1) return theme.palette.error.main; // Super rare (red)
    if (percentage <= 5) return theme.palette.warning.main; // Rare (orange)
    if (percentage <= 15) return theme.palette.info.main; // Uncommon (blue)
    return theme.palette.success.main; // Common (green)
  };

  // Get rarity label based on percentage
  const getRarityLabel = (percentage: number): string => {
    if (percentage <= 1) return "Super Rare";
    if (percentage <= 5) return "Rare";
    if (percentage <= 15) return "Uncommon";
    return "Common";
  };

  const checkRestrictions = (selectedElements: LayerElement[], allLayers: Layer[]): boolean => {
    console.log('Vérification des restrictions pour les éléments:', selectedElements.map(el => el.name));

    // Vérifier les incompatibilités pour chaque élément sélectionné
    for (const element of selectedElements) {
      console.log('Vérification des restrictions pour:', element.name);

      // Vérifier les incompatibilités de l'élément actuel
      if (element.restrictions?.incompatibleWith) {
        console.log('Incompatibilités trouvées pour', element.name, ':', element.restrictions.incompatibleWith);
        
        for (const restriction of element.restrictions.incompatibleWith) {
          const incompatibleLayer = allLayers.find(l => l.id === restriction.layerId);
          if (!incompatibleLayer) {
            console.log('Couche incompatible non trouvée:', restriction.layerId);
            continue;
          }

          console.log('Recherche d\'éléments incompatibles dans la couche:', incompatibleLayer.name);
          
          const selectedElementFromLayer = selectedElements.find(
            el => incompatibleLayer.elements.some(layerEl => layerEl.id === el.id)
          );

          if (selectedElementFromLayer) {
            console.log('Élément sélectionné trouvé dans la couche incompatible:', selectedElementFromLayer.name);
            console.log('IDs incompatibles:', restriction.elementIds);
            console.log('ID de l\'élément sélectionné:', selectedElementFromLayer.id);
            
            if (restriction.elementIds.includes(selectedElementFromLayer.id)) {
              console.log('INCOMPATIBILITÉ TROUVÉE:', element.name, 'est incompatible avec', selectedElementFromLayer.name);
              return false;
            }
          }
        }
      }
    }

    console.log('Aucune incompatibilité trouvée');
    return true;
  };

  const generateNFT = (layers: Layer[]): LayerElement[] => {
    console.log('Début de la génération d\'un NFT');
    const selectedElements: LayerElement[] = [];
    let attempts = 0;
    const maxAttempts = 100; // Pour éviter une boucle infinie

    while (attempts < maxAttempts) {
      console.log(`Tentative ${attempts + 1} de génération`);
      const newSelection: LayerElement[] = [];
      
      for (const layer of layers) {
        console.log(`Sélection dans la couche: ${layer.name}`);
        const totalWeight = layer.elements.reduce((sum: number, el: LayerElement) => sum + el.weight, 0);
        let random = Math.random() * totalWeight;
        
        for (const element of layer.elements) {
          random -= element.weight;
          if (random <= 0) {
            console.log(`Élément sélectionné dans ${layer.name}: ${element.name}`);
            newSelection.push(element);
            break;
          }
        }
      }

      console.log('Vérification des restrictions pour la sélection:', newSelection.map(el => el.name));
      const restrictionsValid = checkRestrictions(newSelection, layers);
      console.log('Résultat de la vérification des restrictions:', restrictionsValid);

      if (restrictionsValid) {
        console.log('Sélection valide trouvée');
        return newSelection;
      }

      attempts++;
    }

    console.log('Impossible de trouver une combinaison valide après', maxAttempts, 'tentatives');
    throw new Error('Unable to generate a valid NFT with current restrictions');
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5" component="h1">
          Collection Generation
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Ready to generate your collection
            </Typography>
            
            <Typography variant="body1" paragraph>
              You are about to generate {config.layerConfigurations[0].growEditionSizeTo} unique NFTs 
              {layers.length > 0 ? ` with ${layers.length} layers` : ''} 
              {uniqueNFTs.length > 0 ? ` including ${uniqueNFTs.length} unique 1/1 NFTs` : ''}.
            </Typography>
            
            {layers.some(layer => layer.elements.length === 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Warning: Some layers have no elements. They will be ignored during generation.
              </Alert>
            )}
          </Grid>
          
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerate}
              disabled={isGenerating || (layers.length === 0 && uniqueNFTs.length === 0)}
              sx={{ mb: 1, width: { xs: '100%', md: 'auto' } }}
            >
              {isGenerating ? 'Generating...' : 'Generate Collection'}
            </Button>
            
            {generatedImages.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleGenerate}
                disabled={isGenerating}
                sx={{ ml: { md: 1 }, width: { xs: '100%', md: 'auto' } }}
              >
                Regenerate
              </Button>
            )}
          </Grid>
        </Grid>
        
        {isGenerating && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Progress: {Math.round(progress)}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </Paper>

      {/* Display pricing information */}
      {generatedImages.length > 0 && (
        <PricingInfo 
          currentTier={currentPricingTier} 
          nftCount={nftCount} 
          isWhitelisted={isWhitelisted}
          walletAddress={walletAddress}
          onConnectWallet={connectWallet}
        />
      )}

      {generatedImages.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Generated Collection ({generatedImages.length} images)
            </Typography>
            
            <Button
              variant="contained"
              color={paymentStatus === PaymentStatus.COMPLETED || isWhitelisted ? "success" : "secondary"}
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isWhitelisted ? (
                  <VerifiedUserIcon />
                ) : needsPayment && paymentStatus !== PaymentStatus.COMPLETED ? (
                  <PaymentIcon />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={() => handleDownload(false)}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 
               isWhitelisted ? 'Download (Whitelisted)' :
               needsPayment && paymentStatus !== PaymentStatus.COMPLETED ? 
               `Pay ${currentPricingTier.price} ETH & Download` : 
               'Download Collection'}
            </Button>
          </Box>
          
          {isWhitelisted && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center">
                <VerifiedUserIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  Your wallet is whitelisted! You can download your collection for free.
                </Typography>
              </Box>
            </Alert>
          )}
          
          {needsPayment && paymentStatus !== PaymentStatus.COMPLETED && !isWhitelisted && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Your collection requires payment before download. Click the button above to complete your purchase.
            </Alert>
          )}
          
          <Tooltip title="The displayed images are previews. Download the collection to get the full resolution images.">
            <Box display="flex" alignItems="center" mb={1}>
              <InfoIcon fontSize="small" color="info" sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Preview of generated images (click to see details)
              </Typography>
            </Box>
          </Tooltip>
          
          <Tooltip title="The download includes a metadata-opensea.csv file compatible with OpenSea's bulk upload format.">
            <Box display="flex" alignItems="center" mb={2}>
              <InfoIcon fontSize="small" color="info" sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                OpenSea-compatible CSV file included in download
              </Typography>
            </Box>
          </Tooltip>
          
          <ImageList cols={4} gap={16} sx={{ maxHeight: 600, overflow: 'auto' }}>
            {generatedImages.map((item, index) => (
              <ImageListItem key={index} onClick={() => handleOpenNFTDetails(item)} sx={{ cursor: 'pointer' }}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={item.imageDataUrl}
                    alt={`NFT #${item.metadata.edition}`}
                    sx={{
                      imageRendering: 'pixelated',
                      objectFit: 'contain'
                    }}
                  />
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {item.metadata.name}
                    </Typography>
                  </CardContent>
                </Card>
              </ImageListItem>
            ))}
          </ImageList>
        </Paper>
      )}

      {/* NFT Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseNFTDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedNFT && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{selectedNFT.metadata.name}</Typography>
              <IconButton onClick={handleCloseNFTDetails} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 400, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    bgcolor: 'rgba(0,0,0,0.03)',
                    borderRadius: 1,
                    p: 2
                  }}>
                    <img 
                      src={selectedNFT.imageDataUrl} 
                      alt={selectedNFT.metadata.name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain',
                        imageRendering: 'pixelated'
                      }} 
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Details</Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Edition:</strong> #{selectedNFT.metadata.edition}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>DNA:</strong> {selectedNFT.metadata.dna.substring(0, 16)}...
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Attributes</Typography>
                  
                  {selectedNFT.metadata.attributes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No attributes found for this NFT.
                    </Typography>
                  ) : (
                    <List dense>
                      {selectedNFT.metadata.attributes.map((attr, index) => {
                        const rarity = calculateAttributeRarity(attr);
                        return (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={attr.trait_type}
                              secondary={attr.value}
                            />
                            <Chip 
                              label={`${getRarityLabel(rarity)} (${rarity.toFixed(1)}%)`}
                              size="small"
                              sx={{ 
                                bgcolor: getRarityColor(rarity),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseNFTDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Modal de paiement */}
      <PaymentModal
        key={`payment-modal-${modalKey}-${nftCount}`}
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onPaymentComplete={handlePaymentComplete}
        pricingTier={currentPricingTier}
        nftCount={nftCount}
        walletAddress={walletAddress}
        setWalletAddress={setWalletAddress}
        onDownload={handleDownload}
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        message={error}
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseError}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Box display="flex" justifyContent="flex-start" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back to Unique NFTs
        </Button>
      </Box>
    </Box>
  );
};

export default GenerateCollection; 